/**
 * Workspace dashboard widgets — parse / serialize helpers.
 *
 * The backend exposes a single `Workspace.dashboardWidgetHtml` string
 * field. The admin UI wants to manage MULTIPLE widgets (each a custom
 * HTML block with an optional title), so we delimit them inside the
 * single field with invisible HTML comments:
 *
 *   <!--WIDGET id="w1" title="Welcome"-->
 *   <p>Hello, team!</p>
 *   <!--/WIDGET-->
 *   <!--WIDGET id="w2" title="">
 *   <iframe src="…"></iframe>
 *   <!--/WIDGET-->
 *
 * The learner dashboard renders the whole string verbatim — the
 * comments are invisible to the browser, so the visible result is
 * exactly the concatenation of each widget's HTML.
 *
 * Migration: a workspace whose `dashboardWidgetHtml` has no markers
 * (set before this multi-widget UI existed) parses as a single
 * untitled widget. Re-saving wraps it in markers, so the next edit
 * sees it as one widget the admin can split or extend.
 *
 * Title supports any character except a literal `"` (which would
 * break the marker). We strip/escape that on serialize.
 */

export interface DashboardWidget {
  /** Stable id for React keys + reordering. Survives parse/serialize. */
  id: string;
  /** Optional short label so admins can tell their widgets apart in
   *  the editor. Not rendered to learners. */
  title: string;
  /** Raw HTML the admin pasted/wrote. Rendered as-is on the learner
   *  dashboard. */
  html: string;
}

const MARKER_OPEN = /<!--WIDGET(?:\s+id="([^"]*)")?(?:\s+title="([^"]*)")?-->/g;
const MARKER_CLOSE = '<!--/WIDGET-->';

export function newWidgetId(): string {
  return `w-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Parse the backend's `dashboardWidgetHtml` string into discrete
 * widgets. Returns at least one widget when input is non-empty (the
 * migration case for legacy single-widget content).
 */
export function parseWidgets(stored: string | undefined | null): DashboardWidget[] {
  const src = (stored ?? '').trim();
  if (!src) return [];

  const widgets: DashboardWidget[] = [];
  let lastIndex = 0;
  // Walk every <!--WIDGET ...--> opener; the close marker is treated
  // as a sentinel (we slice between opener-end and closer-start).
  MARKER_OPEN.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = MARKER_OPEN.exec(src)) !== null) {
    const openerEnd = m.index + m[0].length;
    const closerStart = src.indexOf(MARKER_CLOSE, openerEnd);
    if (closerStart === -1) break;
    const html = src.slice(openerEnd, closerStart).trim();
    const id = m[1] || newWidgetId();
    const title = m[2] || '';
    if (html.length > 0 || title.length > 0) {
      widgets.push({ id, title, html });
    }
    lastIndex = closerStart + MARKER_CLOSE.length;
    MARKER_OPEN.lastIndex = lastIndex;
  }

  // Legacy / pre-marker content — treat the entire blob as one
  // untitled widget so admins don't lose what they had.
  if (widgets.length === 0) {
    widgets.push({ id: newWidgetId(), title: '', html: src });
  }
  return widgets;
}

/** Serialize widgets back into the single backend string. Empty
 *  widgets (no title AND no html) are dropped. Returns '' when the
 *  list is empty — caller should pass `undefined` to the API when
 *  clearing the field entirely. */
export function serializeWidgets(widgets: DashboardWidget[]): string {
  const parts: string[] = [];
  for (const w of widgets) {
    const html = (w.html || '').trim();
    const title = (w.title || '').replace(/"/g, '').trim();
    if (!html && !title) continue;
    parts.push(`<!--WIDGET id="${w.id}" title="${title}"-->\n${html}\n${MARKER_CLOSE}`);
  }
  return parts.join('\n');
}
